import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Search,
    Plus,
    MessageSquare,
    Trash2,
    Download,
    Upload,
    X,
    ChevronLeft,
    Edit2,
    Check,
} from 'lucide-react';
import { useChatStore } from '../stores/chatStore';
import { useUIStore } from '../stores/uiStore';
import { useI18n } from '../i18n';
import { chatApi } from '../lib/api';
import { downloadBlob } from '../lib/utils';
import type { ChatExport } from '../lib/types';

export default function Sidebar() {
    const navigate = useNavigate();
    const location = useLocation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [editingChatId, setEditingChatId] = useState<number | null>(null);
    const [editingTitle, setEditingTitle] = useState('');

    const {
        chats,
        currentChatId,
        searchQuery,
        isLoadingChats,
        loadChats,
        createChat,
        deleteChat,
        setCurrentChat,
        setSearchQuery,
    } = useChatStore();

    const { sidebarOpen, setSidebarOpen } = useUIStore();
    const { formatRelativeTime } = useI18n();

    // Load chats on mount
    useEffect(() => {
        loadChats();
    }, [loadChats]);

    // Handle new chat
    const handleNewChat = async () => {
        const chatId = await createChat();
        if (chatId) {
            // Don't call setCurrentChat - createChat already sets currentChatId and messages
            navigate(`/chat/${chatId}`);
        }
        // Close sidebar on mobile
        if (window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    };

    // Handle chat select
    const handleSelectChat = (id: number) => {
        setCurrentChat(id);
        navigate(`/chat/${id}`);
        // Close sidebar on mobile
        if (window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    };

    // Handle chat delete
    const handleDeleteChat = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        if (confirm('이 채팅을 삭제하시겠습니까?')) {
            await deleteChat(id);
            if (currentChatId === id) {
                navigate('/');
            }
        }
    };

    // Handle export
    const handleExport = async (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        setIsExporting(true);
        try {
            const blob = await chatApi.export(id, 'json');
            downloadBlob(blob, `chat-${id}.json`);
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    // Handle rename
    const handleStartRename = (e: React.MouseEvent, chatItem: typeof chats[0]) => {
        e.stopPropagation();
        setEditingChatId(chatItem.id);
        setEditingTitle(chatItem.title || '');
    };

    const handleSaveRename = async (e: React.MouseEvent | React.KeyboardEvent, id: number) => {
        e.stopPropagation();
        if (editingTitle.trim()) {
            try {
                await chatApi.update(id, { title: editingTitle.trim() });
                await loadChats();
            } catch (error) {
                console.error('Rename failed:', error);
            }
        }
        setEditingChatId(null);
    };

    // Handle import
    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        try {
            const text = await file.text();
            const data: ChatExport = JSON.parse(text);
            const response = await chatApi.import(data);
            if (response.success && response.chat) {
                await loadChats();
                navigate(`/chat/${response.chat.id}`);
            }
        } catch (error) {
            console.error('Import failed:', error);
            alert('가져오기 실패: 올바른 JSON 파일인지 확인하세요.');
        }

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // Get current chat ID from URL
    const urlChatId = location.pathname.match(/\/chat\/(\d+)/)?.[1];
    const activeChatId = urlChatId ? parseInt(urlChatId) : currentChatId;

    return (
        <>
            {/* Mobile overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 md:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-72 bg-dark-900 border-r border-dark-800 
                    flex flex-col transition-transform duration-300 
                    ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
                    md:translate-x-0`}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-dark-800">
                    <h2 className="text-lg font-semibold text-dark-100">채팅 기록</h2>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="btn-icon md:hidden"
                    >
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                </div>

                {/* Search */}
                <div className="p-3">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-500" />
                        <input
                            type="text"
                            placeholder="채팅 검색..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="input pl-10 py-2 text-sm"
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2"
                            >
                                <X className="w-4 h-4 text-dark-500 hover:text-dark-300" />
                            </button>
                        )}
                    </div>
                </div>

                {/* New chat button */}
                <div className="px-3 pb-3">
                    <button
                        onClick={handleNewChat}
                        className="w-full btn-primary py-2.5"
                    >
                        <Plus className="w-4 h-4" />
                        새 채팅
                    </button>
                </div>

                {/* Chat list */}
                <div className="flex-1 overflow-y-auto px-3 pb-3 space-y-1">
                    {isLoadingChats ? (
                        <div className="flex items-center justify-center py-8">
                            <div className="spinner text-accent-500" />
                        </div>
                    ) : chats.length === 0 ? (
                        <div className="text-center py-8">
                            <MessageSquare className="w-8 h-8 text-dark-600 mx-auto mb-2" />
                            <p className="text-sm text-dark-500">
                                {searchQuery ? '검색 결과가 없습니다' : '아직 채팅이 없습니다'}
                            </p>
                        </div>
                    ) : (
                        chats.map((chat) => (
                            <div
                                key={chat.id}
                                onClick={() => handleSelectChat(chat.id)}
                                className={`group relative flex items-start gap-3 p-3 rounded-lg cursor-pointer 
                           transition-all duration-200 ${activeChatId === chat.id
                                        ? 'bg-dark-800'
                                        : 'hover:bg-dark-800/50'
                                    }`}
                            >
                                <MessageSquare className="w-4 h-4 text-dark-500 mt-0.5 flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                    {editingChatId === chat.id ? (
                                        <div className="flex items-center gap-1">
                                            <input
                                                type="text"
                                                value={editingTitle}
                                                onChange={(e) => setEditingTitle(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveRename(e, chat.id);
                                                    if (e.key === 'Escape') setEditingChatId(null);
                                                }}
                                                className="input py-0.5 px-1 text-sm flex-1"
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <button
                                                onClick={(e) => handleSaveRename(e, chat.id)}
                                                className="p-1 rounded hover:bg-dark-700"
                                                title="Save"
                                            >
                                                <Check className="w-3.5 h-3.5 text-green-400" />
                                            </button>
                                            <button
                                                onClick={(e) => { e.stopPropagation(); setEditingChatId(null); }}
                                                className="p-1 rounded hover:bg-dark-700"
                                                title="Cancel"
                                            >
                                                <X className="w-3.5 h-3.5 text-red-400" />
                                            </button>
                                        </div>
                                    ) : (
                                        <>
                                            <p className="text-sm font-medium text-dark-200 truncate">
                                                {chat.title || 'New Chat'}
                                            </p>
                                            <p className="text-xs text-dark-500 mt-0.5">
                                                {formatRelativeTime(chat.updated_at)}
                                            </p>
                                        </>
                                    )}
                                </div>

                                {/* Actions - hide during editing */}
                                {editingChatId !== chat.id && (
                                    <div className="absolute right-2 top-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => handleStartRename(e, chat)}
                                            className="p-1 rounded hover:bg-dark-700"
                                            title="Rename"
                                        >
                                            <Edit2 className="w-3.5 h-3.5 text-dark-400" />
                                        </button>
                                        <button
                                            onClick={(e) => handleExport(e, chat.id)}
                                            disabled={isExporting}
                                            className="p-1 rounded hover:bg-dark-700"
                                            title="Export"
                                        >
                                            <Download className="w-3.5 h-3.5 text-dark-400" />
                                        </button>
                                        <button
                                            onClick={(e) => handleDeleteChat(e, chat.id)}
                                            className="p-1 rounded hover:bg-dark-700"
                                            title="Delete"
                                        >
                                            <Trash2 className="w-3.5 h-3.5 text-red-400" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))
                    )}
                </div>

                {/* Footer - Import */}
                <div className="p-3 border-t border-dark-800">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".json"
                        onChange={handleImport}
                        className="hidden"
                    />
                    <button
                        onClick={() => fileInputRef.current?.click()}
                        className="w-full btn-secondary py-2 text-sm"
                    >
                        <Upload className="w-4 h-4" />
                        채팅 가져오기
                    </button>
                </div>
            </aside>
        </>
    );
}
